import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BTN_PRIMARY_SM, BTN_SECONDARY_SM } from '../buttonStyles';
import { RefundMissedSheet } from './RefundMissedSheet';
import { RebookScreen } from './MissedSingle';
import { useUserIdentity } from '../../hooks/useUserIdentity';
import { useViewerZone } from '../../hooks/useViewerZone';
import { getLiveCallBooking } from '../../services/liveCallBookings';
import { firstNameOf } from '../../utils/liveLessonText';
import type { Message } from '../../types/chat';
import type { LiveCallBooking } from '../../types/liveCall';

/**
 * A support message's actions (docs/booking-contracts.md 2 and 4), as
 * capsules under it: Suggest New Times opens the coach's open times at once (audit 2 Nov, M2), its
 * link only if the lesson can't be read; the server's Pick a New Time opens its link;
 * Refund instead asks once and refunds the held lesson. The first is the
 * message's one fill. Once the lesson is resolved the server removes them
 * and sets `resolved`, and nothing shows.
 */
export function SupportActions({ message }: { message: Message }) {
  const navigate = useNavigate();
  const [refunding, setRefunding] = useState<string | null>(null);
  const [rebooking, setRebooking] = useState<{ bookingId: string; link?: string } | null>(null);
  const actions = message.actions ?? (message.action ? [message.action] : []);
  if (message.resolved || !actions.length) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-2" data-support-actions>
      {actions.map((a, i) => (
        <button key={a.kind} type="button" className={i === 0 ? BTN_PRIMARY_SM : BTN_SECONDARY_SM}
          onClick={() => {
            if (a.kind === 'refund_instead') { if (a.bookingId) setRefunding(a.bookingId); }
            else if (a.kind === 'suggest_new_times' && a.bookingId) setRebooking({ bookingId: a.bookingId, link: a.link });
            else if (a.link) navigate(a.link);
          }}>
          {a.label}
        </button>
      ))}
      {refunding && <RefundMissedSheet bookingId={refunding} onClose={() => setRefunding(null)} />}
      {rebooking && <SupportRebook {...rebooking} onClose={() => setRebooking(null)} />}
    </div>
  );
}

/** Suggest New Times from the Support thread: the picker itself, not the lesson's page with a second button. */
function SupportRebook({ bookingId, link, onClose }: { bookingId: string; link?: string; onClose: () => void }) {
  const navigate = useNavigate();
  const tz = useViewerZone();
  const [b, setB] = useState<LiveCallBooking | null>(null);
  useEffect(() => {
    let live = true;
    getLiveCallBooking(bookingId).then((x) => {
      if (!live) return;
      if (x) setB(x);
      else { onClose(); if (link) navigate(link); }
    }).catch(() => { if (live) { onClose(); if (link) navigate(link); } });
    return () => { live = false; };
  }, [bookingId]); // eslint-disable-line react-hooks/exhaustive-deps
  const coach = useUserIdentity(b?.coachId, 'coach');
  if (!b) return null;
  const coachName = coach?.name ?? b.coachName ?? 'your coach';
  return <RebookScreen b={b} coachName={coachName} coachFirst={firstNameOf(coachName, 'your coach')} photo={coach?.avatarUrl} tz={tz} onClose={onClose} />;
}
